import * as THREE from "three";
import { closeSeams } from "./spinSeams";

/**
 * The parts that turn round and round in a model's looping clips — the Moonstone Keep's shards, star rings and moon,
 * the fortress coils and crown, Starfall's halos, the Arcane tower's gyroscope rings, a rare enemy's orbs — found from
 * the baked clips and taken off them, so the rig turns them by code instead (spinDriver.ts).
 *
 * Owner report 2026-10-24: "the moonstone keep (and maybe the towers) with the crystals that spin around the top, when
 * they finish their animation they snap so it's not a smooth circle around." The bakes turn such a part a fraction of
 * a turn each loop (a quarter for four shards, an eighth for eight star beads, a sixth for a nimbus) and jump back,
 * counting on the part looking the same after that fraction. It does not: the albedo carries a painted key light and
 * baked AO, the shards bob, and every other clip (charge, fire, show) restarts the turn from the clip's own first key.
 * A clip cannot hold an endless turn, so the turn leaves the clips: each loop clip gives the part a rate (and keeps any
 * wobble of its axis, which does close), each one-shot plays on top as a change from where it found the part, and the
 * angle only ever accumulates.
 */

/** Clips the rig plays as loops (AnimRig.playLoop's names): a part turning in one of them is a spinner. */
export const SPIN_LOOPS: ReadonlySet<string> = new Set(["idle", "walk", "fly", "glide", "channel", "opened", "shake"]);

/** How far a part must turn in one pass of a loop clip, always the same way, to count as spinning (rad). */
export const MIN_TURN = THREE.MathUtils.degToRad(20);
/** A loop's tilt of the spin axis (its wobble) must come back to within this at the loop's end (rad). */
const SWING_SEAM = THREE.MathUtils.degToRad(1);
/** A wobble smaller than this anywhere in the loop is key noise (the packed quaternions' ~0.7°), not kept (rad). */
const SWING_KEEP = THREE.MathUtils.degToRad(1.5);
/** A spin turns at least this many times further than its axis wobbles; less is a swing (a bell, a lantern). */
const SPIN_OVER_WOBBLE = 2;
/** A loop that rocks the part back and forth about the spin axis by more than this is not a spin (rad). */
const ROCK_SLACK = THREE.MathUtils.degToRad(2);
/** A key may step back this much and the turn still count as one way (rad). */
const STEP_SLACK = THREE.MathUtils.degToRad(0.5);

/** A spinner in one loop clip: its rate (rad per second of clip time) and the wobble of its axis (null: none). */
export interface LoopSpin { readonly rate: number; readonly swing: THREE.Interpolant | null }

export interface Spinner {
  readonly node: string;
  /** Its pose at angle 0 (the reference loop's first key) and the axis it turns about, in its own frame. */
  readonly base: THREE.Quaternion;
  readonly axis: THREE.Vector3;
  /** The loop clips that key it. */
  readonly loops: ReadonlyMap<string, LoopSpin>;
  /** The one-shot clips that key it: their rotation, played on top as a change from its first sampled pose. */
  readonly shots: ReadonlyMap<string, THREE.Interpolant>;
  /** The fastest any of its one-shots turns it (rad per second of clip time): the tests' bound. */
  readonly shotSpeed: number;
}

export interface SpinTable { readonly spinners: readonly Spinner[]; readonly clips: readonly THREE.AnimationClip[] }

const tables = new WeakMap<readonly THREE.AnimationClip[], SpinTable>();

/** The spinners of a model's clips and the clips without their turns (cached per clip list). */
export function spinTable(clips: readonly THREE.AnimationClip[]): SpinTable {
  let t = tables.get(clips);
  if (!t) { t = buildTable(clips); tables.set(clips, t); }
  return t;
}

function buildTable(clips: readonly THREE.AnimationClip[]): SpinTable {
  const nodes = new Set<string>();
  for (const c of clips) if (SPIN_LOOPS.has(c.name)) for (const tr of c.tracks) if (tr.name.endsWith(".quaternion")) nodes.add(nodeOf(tr.name));
  const spinners: Spinner[] = [];
  for (const n of nodes) { const s = classify(n, clips); if (s) spinners.push(s); }
  if (spinners.length === 0) return { spinners, clips };
  const names = new Set(spinners.map((s) => `${s.node}.quaternion`));
  const out = clips.map((c) => {
    const tracks = c.tracks.filter((tr) => !names.has(tr.name));
    const stripped = tracks.length === c.tracks.length ? c : new THREE.AnimationClip(c.name, c.duration, tracks, c.blendMode);
    if (!SPIN_LOOPS.has(c.name)) return stripped;
    return closeSeams(stripped, new Set(spinners.filter((s) => (s.loops.get(c.name)?.rate ?? 0) !== 0).map((s) => s.node)));
  });
  return { spinners, clips: out };
}

export const nodeOf = (track: string): string => track.slice(0, track.lastIndexOf("."));
const linearQuat = (tr: THREE.KeyframeTrack): boolean => tr.values.length === tr.times.length * 4;

const _q = new THREE.Quaternion(), _d = new THREE.Quaternion(), _t = new THREE.Quaternion(), _s = new THREE.Quaternion();

/**
 * The node is a spinner when a loop turns it one way by MIN_TURN about one axis (any wobble of that axis closing over
 * the loop), and every loop that keys it either turns it about that axis or holds it.
 */
function classify(node: string, clips: readonly THREE.AnimationClip[]): Spinner | null {
  const name = `${node}.quaternion`;
  const loops = clips.filter((c) => SPIN_LOOPS.has(c.name)).sort((a, b) => Number(b.name === "idle") - Number(a.name === "idle"));
  const tracks = clips.map((c) => c.tracks.find((tr) => tr.name === name));
  if (tracks.some((tr) => tr && !linearQuat(tr))) return null;
  for (const ref of loops) {
    const tr = ref.tracks.find((x) => x.name === name);
    if (!tr) continue;
    const base = new THREE.Quaternion().fromArray(tr.values, 0);
    const axis = axisOf(tr, base);
    if (!axis) continue;
    const spins = new Map<string, LoopSpin>();
    for (const c of loops) {
      const k = c.tracks.find((x) => x.name === name);
      const s = k ? loopSpin(k, base, axis, c.duration) : null;
      if (k && !s) { spins.clear(); break; }
      if (s) spins.set(c.name, s);
    }
    if ((spins.get(ref.name)?.rate ?? 0) === 0) continue;
    const shots = new Map<string, THREE.Interpolant>();
    let shotSpeed = 0;
    clips.forEach((c, i) => {
      const k = tracks[i];
      if (!k || SPIN_LOOPS.has(c.name)) return;
      shots.set(c.name, k.createInterpolant());
      shotSpeed = Math.max(shotSpeed, speedOf(k));
    });
    return { node, base, axis, loops: spins, shots, shotSpeed };
  }
  return null;
}

/** The axis of the track's largest turn from `base`, or null when it never turns MIN_TURN / 2. */
function axisOf(tr: THREE.KeyframeTrack, base: THREE.Quaternion): THREE.Vector3 | null {
  const inv = base.clone().invert();
  let best = 0, axis: THREE.Vector3 | null = null;
  for (let i = 0; i < tr.times.length; i++) {
    _d.copy(inv).multiply(_q.fromArray(tr.values, i * 4));
    if (_d.w < 0) _d.set(-_d.x, -_d.y, -_d.z, -_d.w);
    const a = 2 * Math.acos(Math.min(1, _d.w));
    if (a > best) { best = a; axis = new THREE.Vector3(_d.x, _d.y, _d.z); }
  }
  return best >= MIN_TURN / 2 && axis ? axis.normalize() : null;
}

/**
 * A loop track as a turn about `axis` from `base` (twist first, then the axis' wobble): its one-way rate, or 0 when it
 * holds still; null when it rocks about the axis or its wobble does not close.
 */
function loopSpin(tr: THREE.KeyframeTrack, base: THREE.Quaternion, axis: THREE.Vector3, length: number): LoopSpin | null {
  const inv = base.clone().invert(), n = tr.times.length, angles = new Float64Array(n), swings = new Float32Array(n * 4);
  let prev = 0, wobble = 0;
  for (let i = 0; i < n; i++) {
    _d.copy(inv).multiply(_q.fromArray(tr.values, i * 4));
    const p = _d.x * axis.x + _d.y * axis.y + _d.z * axis.z;
    const a = 2 * Math.atan2(p, _d.w);
    _t.set(axis.x * p, axis.y * p, axis.z * p, _d.w).normalize();
    _s.copy(_t).invert().multiply(_d);                       // d = twist · swing
    if (_s.w < 0) _s.set(-_s.x, -_s.y, -_s.z, -_s.w);
    _s.toArray(swings, i * 4);
    wobble = Math.max(wobble, 2 * Math.acos(Math.min(1, _s.w)));
    angles[i] = i === 0 ? a : angles[i - 1] + Math.atan2(Math.sin(a - prev), Math.cos(a - prev));
    prev = a;
  }
  _q.fromArray(swings, 0); _s.fromArray(swings, (n - 1) * 4);
  if (_q.angleTo(_s) > SWING_SEAM) return null;
  const net = oneWay(angles);
  if (net === 0 && Math.max(...angles) - Math.min(...angles) > ROCK_SLACK) return null;
  if (net !== 0 && Math.abs(net) < SPIN_OVER_WOBBLE * wobble) return null;
  const swing = wobble > SWING_KEEP ? new THREE.QuaternionKeyframeTrack("swing.quaternion", Array.from(tr.times), Array.from(swings)).createInterpolant() : null;
  return { rate: length > 0 ? net / length : 0, swing };
}

/** The net turn when the angles go one way by at least MIN_TURN, else 0. */
function oneWay(angles: Float64Array): number {
  const net = angles[angles.length - 1] - angles[0];
  if (Math.abs(net) < MIN_TURN) return 0;
  const s = Math.sign(net);
  for (let i = 1; i < angles.length; i++) if ((angles[i] - angles[i - 1]) * s < -STEP_SLACK) return 0;
  return net;
}

/** The fastest a quaternion track turns between two keys (rad/s). */
function speedOf(tr: THREE.KeyframeTrack): number {
  let m = 0;
  for (let i = 1; i < tr.times.length; i++) {
    const dt = tr.times[i] - tr.times[i - 1];
    if (dt > 0) m = Math.max(m, _q.fromArray(tr.values, (i - 1) * 4).angleTo(_d.fromArray(tr.values, i * 4)) / dt);
  }
  return m;
}
